import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHeader, DataTable, Modal, Badge, EmptyState, FormSection } from '../components/ui/SharedUI';
import InsuranceForm from '../components/forms/InsuranceForm';
import PropertyDocumentActions from '../components/documents/PropertyDocumentActions';
import { demoRepository } from '../repository/demoRepository';
import { calculations } from '../utils/calculations';
import { useFeedback } from '../components/ui/FeedbackUI';
import SafeIcon from '../common/SafeIcon';

const InsurancePage = () => {
  const { showToast, showConfirm } = useFeedback();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingInsurance, setEditingInsurance] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [propertyFilter, setPropertyFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);
  const [editingInsuranceDocument, setEditingInsuranceDocument] = useState(null);
  const [documentEditForm, setDocumentEditForm] = useState({ title: '', notes: '' });
  const [isSavingInsuranceDocument, setIsSavingInsuranceDocument] = useState(false);

  useEffect(() => {
    const handleDataRefreshed = () => {
      setRefreshTrigger(prev => prev + 1);
    };
    window.addEventListener('mpp:data-refreshed', handleDataRefreshed);
    return () => window.removeEventListener('mpp:data-refreshed', handleDataRefreshed);
  }, []);

  const allInsurance = useMemo(() => {
    return demoRepository.getAllInsurancePolicies();
  }, [refreshTrigger]);

  const allDocuments = useMemo(() => {
    return demoRepository.getDocuments();
  }, [refreshTrigger]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const policyId = params.get('policy');
    if (!policyId) return;
    const policy = allInsurance.find(item => item.id === policyId);
    if (!policy) return;
    setEditingInsurance(policy);
    setIsAddModalOpen(true);
    navigate('/insurance', { replace: true });
  }, [location.search, allInsurance, navigate]);

  const properties = demoRepository.getAllProperties().filter(p => p.status === 'Active');

  const filteredInsurance = useMemo(() => {
    return allInsurance.filter(ins => {
      if (statusFilter !== 'All' && ins.status !== statusFilter) return false;
      if (propertyFilter !== 'All' && ins.propertyId !== propertyFilter) return false;
      if (typeFilter !== 'All' && ins.policyType !== typeFilter) return false;
      
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const property = ins.propertyId ? demoRepository.getPropertyById(ins.propertyId) : null;
        return (
          ins.provider?.toLowerCase().includes(lower) ||
          ins.policyNumber?.toLowerCase().includes(lower) ||
          property?.name?.toLowerCase().includes(lower)
        );
      }
      return true;
    });
  }, [allInsurance, searchTerm, statusFilter, propertyFilter, typeFilter]);

  const totals = useMemo(() => {
    const activePolicies = filteredInsurance.filter(i => i.status === 'active');
    const totalPremium = activePolicies.reduce((sum, i) => sum + i.premium, 0);
    const totalCoverage = activePolicies.reduce((sum, i) => sum + Number(i.coverage || 0), 0);
    
    const expiringSoon = activePolicies.filter(i => {
      const days = calculations.calculateDaysToExpiry(i.expiryDate);
      return days > 0 && days <= 30;
    }).length;
    
    const expired = activePolicies.filter(i => calculations.calculateDaysToExpiry(i.expiryDate) < 0).length;
    
    return {
      totalPremium,
      totalCoverage,
      activeCount: activePolicies.length,
      expiringSoon,
      expired
    };
  }, [filteredInsurance]);

  const policyListSummary = useMemo(() => {
    return filteredInsurance.reduce((summary, policy) => {
      const days = calculations.calculateDaysToExpiry(policy.expiryDate);
      const status = String(policy.status || '').toLowerCase();
      if (status === 'active') summary.active += 1;
      if (status === 'claim_open') summary.claimOpen += 1;
      if (days >= 0 && days <= 30) summary.dueWithin30 += 1;
      if (days < 0) summary.expired += 1;
      return summary;
    }, { active: 0, claimOpen: 0, dueWithin30: 0, expired: 0 });
  }, [filteredInsurance]);

  const handleAddInsurance = useCallback(async (data) => {
    if (isSavingInsurance) return;
    setIsSavingInsurance(true);
    try {
      const { attachment, ...policyData } = data;
      const result = editingInsurance
        ? await demoRepository.updateInsurance(editingInsurance.id, policyData)
        : await demoRepository.addInsurance(policyData);

      if (!result?.success) {
        showToast(result?.error || 'Insurance policy save failed.', 'error');
        return;
      }

      const savedPolicy = result.record || { ...policyData, id: editingInsurance?.id };
      let documentSaved = false;

      if (attachment?.file) {
        const file = attachment.file;
        const documentResult = await demoRepository.addDocument({
          title: attachment.title || file.name.replace(/\\.[^/.]+$/, ''),
          documentType: attachment.documentType || 'insurance_certificate',
          status: 'active',
          propertyId: policyData.propertyId,
          insurancePolicyId: savedPolicy.id,
          documentDate: attachment.documentDate || policyData.startDate || Date.now(),
          financialYear: '',
          tags: ['insurance', policyData.policyType].filter(Boolean),
          notes: `Insurance policy document · ${policyData.provider || 'Provider'} · ${policyData.policyNumber || 'Policy'}`,
          files: [file],
          fileName: file.name,
          fileType: file.type || file.name.split('.').pop()?.toUpperCase() || '',
          fileSize: file.size || 0,
          source: 'upload',
          attachmentMode: 'link',
          metadata: {
            uploadedFrom: 'InsurancePage',
            fileName: file.name,
            fileType: file.type || '',
            fileSize: file.size || 0,
            insuranceProvider: policyData.provider || '',
            insurancePolicyNumber: policyData.policyNumber || '',
            insurancePolicyType: policyData.policyType || ''
          }
        });

        if (!documentResult?.success) {
          if (result.mode === 'pocketbase') {
            try {
              await demoRepository.refreshFromPocketBase({ force: true, silent: true, notify: true });
            } catch {}
          }
          setEditingInsurance(savedPolicy);
          setRefreshTrigger(prev => prev + 1);
          window.dispatchEvent(new CustomEvent('mpp:data-refreshed', { detail: { source: 'insurance-policy-save' } }));
          showToast(
            documentResult?.cancelled
              ? 'Insurance policy saved. The PDF upload was paused so you can choose a smaller/compressed copy and attach it again.'
              : `Insurance policy saved, but the document upload failed: ${documentResult?.error || 'Unknown document error'}. Please retry the attachment.`,
            documentResult?.cancelled ? 'info' : 'error'
          );
          return;
        }
        documentSaved = true;
      }

      if (result.mode === 'pocketbase') {
        try {
          await demoRepository.refreshFromPocketBase({ force: true, silent: true, notify: true });
        } catch {
          window.dispatchEvent(new CustomEvent('mpp:data-refreshed', { detail: { source: 'insurance-policy-save' } }));
        }
      } else {
        window.dispatchEvent(new CustomEvent('mpp:data-refreshed', { detail: { source: 'insurance-policy-save' } }));
      }

      setRefreshTrigger(prev => prev + 1);
      showToast(
        documentSaved
          ? 'Insurance policy and document saved. The document is now linked to Global Documents and the property.'
          : (result.mode === 'pocketbase' ? 'Insurance policy saved to PocketBase.' : 'Insurance policy saved to demo data.'),
        'success'
      );
      setIsAddModalOpen(false);
      setEditingInsurance(null);
    } catch (error) {
      showToast(error?.message || 'Insurance policy save failed.', 'error');
    } finally {
      setIsSavingInsurance(false);
    }
  }, [editingInsurance, isSavingInsurance, showToast]);

  const handleArchiveInsurance = useCallback((id) => {
    showConfirm({
      title: 'Archive Insurance Policy?',
      message: 'Are you sure you want to archive this insurance policy? This will not delete historical data.',
      confirmText: 'Archive',
      isDestructive: true,
      onConfirm: async () => {
        try {
          const result = await demoRepository.updateInsurance(id, { status: 'archived' });
          if (result?.success) {
            showToast('Insurance policy archived', 'info');
            setRefreshTrigger(prev => prev + 1);
          } else {
            showToast(result?.error || 'Archive failed.', 'error');
          }
        } catch (error) {
          showToast(error?.message || 'Archive failed.', 'error');
        }
      }
    });
  }, [showConfirm, showToast]);

  const handleEditInsurance = useCallback((insurance) => {
    setEditingInsurance(insurance);
    setIsAddModalOpen(true);
  }, []);

  const getDocumentOpenUrl = useCallback((doc) => {
    if (!doc) return '';
    const fileNames = Array.isArray(doc.files) && doc.files.length > 0
      ? doc.files.filter(Boolean)
      : Array.isArray(doc.fileNames) && doc.fileNames.length > 0
        ? doc.fileNames.filter(Boolean)
        : doc.fileName ? [doc.fileName] : [];
    const baseUrl = (import.meta.env.VITE_POCKETBASE_URL || '').trim().replace(/\/$/, '');
    const mappedUrl = Array.isArray(doc.fileUrls)
      ? doc.fileUrls.find(url => typeof url === 'string' && url.length > 0)
      : '';
    if (mappedUrl?.startsWith('http')) return mappedUrl;
    if (mappedUrl?.startsWith('/') && baseUrl) return `${baseUrl}${mappedUrl}`;
    if (baseUrl && doc.id && fileNames.length > 0) {
      return `${baseUrl}/api/files/documents/${doc.id}/${encodeURIComponent(String(fileNames[0]))}`;
    }
    return '';
  }, []);

  const handleEditInsuranceDocument = useCallback((doc) => {
    setEditingInsuranceDocument(doc);
    setDocumentEditForm({ title: doc?.title || doc?.fileName || '', notes: doc?.notes || '' });
  }, []);

  const handleSaveInsuranceDocument = useCallback(async (event) => {
    event.preventDefault();
    if (!editingInsuranceDocument || isSavingInsuranceDocument) return;
    const title = documentEditForm.title.trim();
    if (!title) {
      showToast('Document title is required.', 'error');
      return;
    }
    setIsSavingInsuranceDocument(true);
    try {
      const doc = editingInsuranceDocument;
      const payload = {
        title,
        notes: documentEditForm.notes,
        documentType: doc.documentType || 'insurance_certificate',
        status: doc.status || 'active',
        propertyId: doc.propertyId || '',
        propertyAgentAssignmentId: doc.propertyAgentAssignmentId || '',
        spaceId: doc.spaceId || '',
        tenantId: doc.tenantId || '',
        leaseId: doc.leaseId || '',
        transactionId: doc.transactionId || '',
        inspectionId: doc.inspectionId || '',
        insurancePolicyId: doc.insurancePolicyId || doc.insuranceId || '',
        loanId: doc.loanId || '',
        openItemId: doc.openItemId || '',
        documentDate: doc.documentDate || doc.uploadedDate || Date.now(),
        financialYear: doc.financialYear || '',
        tags: Array.isArray(doc.tags) ? doc.tags : [],
        visibility: doc.visibility || 'private',
        attachmentMode: doc.attachmentMode || 'link',
        source: doc.source || 'upload',
        externalLinks: Array.isArray(doc.externalLinks) ? doc.externalLinks : [],
        attachmentRefs: Array.isArray(doc.attachmentRefs) ? doc.attachmentRefs : [],
        archived: !!doc.archived,
        metadata: doc.metadata || {},
        extraData: doc.extraData || null,
        adminNotes: doc.adminNotes || ''
      };
      const result = await demoRepository.updateDocument(doc.id, payload);
      if (!result?.success) {
        showToast(result?.error || 'Document update failed.', 'error');
        return;
      }
      if (result.mode === 'pocketbase') {
        try {
          await demoRepository.refreshFromPocketBase({ force: true, silent: true, notify: true });
        } catch {
          window.dispatchEvent(new CustomEvent('mpp:data-refreshed', { detail: { source: 'insurance-document-update' } }));
        }
      } else {
        window.dispatchEvent(new CustomEvent('mpp:data-refreshed', { detail: { source: 'insurance-document-update' } }));
      }
      setRefreshTrigger(prev => prev + 1);
      setEditingInsuranceDocument(null);
      showToast('Insurance document updated.', 'success');
    } catch (error) {
      showToast(error?.message || 'Document update failed.', 'error');
    } finally {
      setIsSavingInsuranceDocument(false);
    }
  }, [editingInsuranceDocument, isSavingInsuranceDocument, documentEditForm, showToast]);

  const getExpiryStatus = (expiryDate) => {
    const days = calculations.calculateDaysToExpiry(expiryDate);
    if (days < 0) return { label: 'Expired', color: 'text-red-600', bg: 'bg-red-50' };
    if (days <= 30) return { label: 'Expiring Soon', color: 'text-amber-600', bg: 'bg-amber-50' };
    return { label: 'Active', color: 'text-emerald-600', bg: 'bg-emerald-50' };
  };

  const statusColors = {
    active: 'active',
    renewal_due: 'pending',
    expired: 'overdue',
    cancelled: 'default',
    claim_open: 'overdue',
    archived: 'default'
  };

  const policyTypeLabels = {
    building: 'Building',
    contents: 'Contents',
    landlord: 'Landlord',
    strata: 'Strata',
    public_liability: 'Public Liability',
    other: 'Other'
  };

  const columns = [
    'Property', 'Provider', 'Policy #', 'Type', 'Premium', 'Coverage', 'Expiry', 'Status', 'Actions'
  ];

  const getLinkedDocuments = (insuranceId) => allDocuments.filter(doc =>
    String(doc.insurancePolicyId || doc.insuranceId || '') === String(insuranceId) &&
    String(doc.status || '').toLowerCase() !== 'archived'
  );

  const renderRow = (ins) => {
    const property = ins.propertyId ? demoRepository.getPropertyById(ins.propertyId) : null;
    const expiryStatus = getExpiryStatus(ins.expiryDate);
    const daysToExpiry = calculations.calculateDaysToExpiry(ins.expiryDate);
    const linkedDocuments = getLinkedDocuments(ins.id);
    
    return [
      property?.name || '-',
      <div>
        <div className="font-medium text-slate-900">{ins.provider}</div>
        <div className="text-xs text-slate-500">{ins.policyNumber}</div>
      </div>,
      ins.policyNumber,
      <Badge label={policyTypeLabels[ins.policyType] || ins.policyType} status="default" />,
      <div className="font-medium">{calculations.formatCurrencyAUD(ins.premium)}/yr</div>,
      calculations.formatCurrencyAUD(Number(ins.coverage || 0)),
      <div>
        <div className="font-medium text-slate-900">{calculations.formatDateAU(ins.expiryDate)}</div>
        <div className={`text-xs ${expiryStatus.color}`}>
          {daysToExpiry > 0 ? `${daysToExpiry} days` : daysToExpiry === 0 ? 'Today' : 'Expired'}
        </div>
      </div>,
      <Badge label={String(ins.status || '').replace(/_/g, ' ')} status={statusColors[ins.status] || 'default'} />,
      <div className="min-w-[220px] space-y-2">
        {linkedDocuments.map(doc => (
          <div key={doc.id} className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-[11px] font-semibold text-slate-700" title={doc.title || doc.fileName || 'Insurance document'}>{doc.title || doc.fileName || 'Insurance document'}</div>
                {doc.notes ? <div className="truncate text-[10px] text-slate-400" title={doc.notes}>Notes available</div> : null}
              </div>
            </div>
            <PropertyDocumentActions
              doc={doc}
              openUrl={getDocumentOpenUrl(doc)}
              onEdit={() => handleEditInsuranceDocument(doc)}
              onViewInDocuments={() => navigate('/documents')}
            />
          </div>
        ))}
        {linkedDocuments.length === 0 && <div className="text-[11px] text-slate-400">No linked document</div>}
        <div className="flex gap-2 pt-1">
          <button 
            onClick={(e) => { e.stopPropagation(); handleEditInsurance(ins); }}
            className="text-amber-600 hover:underline text-xs font-medium"
          >
            Edit Policy
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); handleArchiveInsurance(ins.id); }}
            className="text-red-600 hover:underline text-xs font-medium"
          >
            Archive
          </button>
        </div>
      </div>
    ];
  };

  const renderInsuranceCard = (ins) => {
    const property = ins.propertyId ? demoRepository.getPropertyById(ins.propertyId) : null;
    const daysToExpiry = calculations.calculateDaysToExpiry(ins.expiryDate);
    const linkedDocuments = getLinkedDocuments(ins.id);
    const expiryTone = daysToExpiry < 0
      ? 'border-red-200 bg-red-50 text-red-800'
      : daysToExpiry <= 30
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-emerald-200 bg-emerald-50 text-emerald-800';
    const expiryLabel = daysToExpiry < 0
      ? `Expired ${Math.abs(daysToExpiry)} day${Math.abs(daysToExpiry) === 1 ? '' : 's'} ago`
      : daysToExpiry === 0
        ? 'Expires today'
        : `${daysToExpiry} days remaining`;

    return (
      <article key={ins.id} className="flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
        <div className="min-w-0 flex-1 p-3.5 sm:p-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm sm:h-12 sm:w-12">
              <SafeIcon icon="Shield" className="text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-[15px] font-bold leading-5 text-slate-900 sm:text-base">{ins.provider || 'Insurance provider'}</h3>
                  <p className="mt-0.5 truncate text-[11px] leading-4 text-slate-500 sm:text-xs">{ins.policyNumber || 'No policy number'}</p>
                </div>
                <div className="shrink-0">
                  <Badge label={String(ins.status || '').replace(/_/g, ' ')} status={statusColors[ins.status] || 'default'} />
                </div>
              </div>
              <div className="mt-2 flex min-w-0 items-center gap-1.5 text-[11px] text-slate-500">
                <SafeIcon icon="Home" className="shrink-0 text-slate-400" />
                <span className="truncate font-medium text-slate-700">{property?.name || 'No property linked'}</span>
                <span className="shrink-0 text-slate-300">·</span>
                <span className="truncate">{policyTypeLabels[ins.policyType] || ins.policyType || 'Policy'}</span>
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4">
            <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2.5">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 sm:text-[10px]">Annual premium</div>
              <div className="mt-1 truncate text-sm font-bold text-slate-900">{calculations.formatCurrencyAUD(Number(ins.premium || 0))}</div>
              <div className="mt-0.5 truncate text-[10px] text-slate-500 sm:text-[11px]">{calculations.formatCurrencyAUD(Number(ins.premium || 0) / 12)}/month</div>
            </div>
            <div className="min-w-0 rounded-xl bg-blue-50 px-3 py-2.5">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-blue-600 sm:text-[10px]">Coverage</div>
              <div className="mt-1 truncate text-sm font-bold text-blue-900">{calculations.formatCurrencyAUD(Number(ins.coverage || 0))}</div>
              <div className="mt-0.5 truncate text-[10px] text-blue-700 sm:text-[11px]">Excess {calculations.formatCurrencyAUD(Number(ins.excess || 0))}</div>
            </div>
          </div>

          <div className={`mt-2 rounded-xl border px-3 py-2.5 ${expiryTone}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[9px] font-semibold uppercase tracking-wide opacity-70 sm:text-[10px]">Policy expiry</div>
                <div className="mt-1 font-bold">{calculations.formatDateAU(ins.expiryDate)}</div>
              </div>
              <div className="shrink-0 text-right text-[11px] font-semibold">{expiryLabel}</div>
            </div>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 sm:text-[10px]">Start date</div>
              <div className="mt-1 truncate text-xs font-semibold text-slate-800 sm:text-sm">{calculations.formatDateAU(ins.startDate)}</div>
            </div>
            <div className="min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 sm:text-[10px]">Documents</div>
              <div className="mt-1 text-xs font-semibold text-slate-800 sm:text-sm">{linkedDocuments.length} linked</div>
            </div>
          </div>

          <div className="mt-3 border-t border-slate-100 pt-3">
            {linkedDocuments.length > 0 ? (
              <div className="space-y-2">
                {linkedDocuments.map(doc => (
                  <div key={doc.id} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-[11px] font-semibold text-slate-700" title={doc.title || doc.fileName || 'Insurance document'}>{doc.title || doc.fileName || 'Insurance document'}</div>
                      {doc.notes ? <div className="mt-0.5 truncate text-[10px] text-slate-400" title={doc.notes}>Notes available</div> : null}
                    </div>
                    <div className="mt-2">
                      <PropertyDocumentActions
                        doc={doc}
                        openUrl={getDocumentOpenUrl(doc)}
                        onEdit={() => handleEditInsuranceDocument(doc)}
                        onViewInDocuments={() => navigate('/documents')}
                        touchFriendly
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-center text-[11px] text-slate-400">No linked policy document</div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 border-t border-slate-100 bg-slate-50/60 p-3">
          <button
            type="button"
            onClick={() => handleEditInsurance(ins)}
            className="min-h-[44px] rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            Edit Policy
          </button>
          <button
            type="button"
            onClick={() => handleArchiveInsurance(ins.id)}
            className="min-h-[44px] rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400"
          >
            Archive
          </button>
        </div>
      </article>
    );
  };

  return (
    <div className="min-w-0 animate-fade-in">
      <PageHeader 
        title="Insurance Policies" 
        subtitle="Track property insurance coverage, premiums, and renewal dates."
        actionLabel="Add Policy"
        onAction={() => { setEditingInsurance(null); setIsAddModalOpen(true); }}
        compact
        mobileActionFullWidth
      />

      <FormSection title="Insurance Summary" className="mb-4 sm:mb-5" compact>
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4 lg:gap-3">
          <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">Active Policies</div>
            <div className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">{totals.activeCount}</div>
            <div className="mt-1 truncate text-[10px] text-slate-500 sm:text-xs">{calculations.formatCurrencyAUD(totals.totalCoverage)} coverage</div>
          </div>
          <div className="min-w-0 rounded-xl border border-emerald-200 bg-emerald-50 p-3 sm:p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 sm:text-xs">Annual Premium</div>
            <div className="mt-1 truncate text-xl font-bold text-emerald-900 sm:text-2xl">{calculations.formatCurrencyAUD(totals.totalPremium)}</div>
            <div className="mt-1 truncate text-[10px] text-emerald-700 sm:text-xs">{calculations.formatCurrencyAUD(totals.totalPremium / 12)}/month</div>
          </div>
          <div className="min-w-0 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 sm:text-xs">Expiring Soon</div>
            <div className="mt-1 text-xl font-bold text-amber-900 sm:text-2xl">{totals.expiringSoon}</div>
            <div className="mt-1 text-[10px] text-amber-700 sm:text-xs">Within 30 days</div>
          </div>
          <div className="min-w-0 rounded-xl border border-red-200 bg-red-50 p-3 sm:p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-red-700 sm:text-xs">Expired</div>
            <div className="mt-1 text-xl font-bold text-red-900 sm:text-2xl">{totals.expired}</div>
            <div className="mt-1 text-[10px] text-red-700 sm:text-xs">Action required</div>
          </div>
        </div>
      </FormSection>

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:mb-5 sm:p-4">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-[minmax(0,1.6fr)_minmax(150px,0.65fr)_minmax(150px,0.65fr)_minmax(180px,0.8fr)_auto]">
          <label className="relative block min-w-0 sm:col-span-2 xl:col-span-1">
            <span className="sr-only">Search insurance policies</span>
            <SafeIcon icon="Search" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search provider, policy number or property"
              className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-400 focus:border-amber-400 focus:bg-white focus:ring-2 focus:ring-amber-500"
            />
          </label>

          <label className="block min-w-0">
            <span className="sr-only">Filter by status</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-500">
              <option value="All">All Statuses</option>
              <option value="active">Active</option>
              <option value="renewal_due">Renewal Due</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
              <option value="claim_open">Claim Open</option>
              <option value="archived">Archived</option>
            </select>
          </label>

          <label className="block min-w-0">
            <span className="sr-only">Filter by policy type</span>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-500">
              <option value="All">All Types</option>
              {Object.entries(policyTypeLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </label>

          <label className="block min-w-0">
            <span className="sr-only">Filter by property</span>
            <select value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-500">
              <option value="All">All Properties</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('All');
              setPropertyFilter('All');
              setTypeFilter('All');
            }}
            className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 sm:col-span-2 xl:col-span-1"
          >
            Clear
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-3 text-[11px] leading-4 text-slate-500">
          <span className="font-semibold text-slate-700">{filteredInsurance.length} polic{filteredInsurance.length === 1 ? 'y' : 'ies'} shown</span>
          <span>{policyListSummary.active} active</span>
          <span className={policyListSummary.dueWithin30 > 0 ? 'font-medium text-amber-700' : ''}>{policyListSummary.dueWithin30} due within 30 days</span>
          {policyListSummary.claimOpen > 0 && <span className="font-medium text-red-700">{policyListSummary.claimOpen} claim open</span>}
        </div>
      </div>

      {filteredInsurance.length > 0 ? (
        <>
          <div className="hidden 2xl:block">
            <DataTable 
              columns={columns} 
              data={filteredInsurance} 
              renderRow={renderRow} 
              emptyMessage="No insurance policies found matching your criteria."
            />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:hidden">
            {filteredInsurance.map(renderInsuranceCard)}
          </div>
        </>
      ) : (
        <EmptyState 
          title="No Insurance Policies Recorded"
          description="Add insurance policies to track coverage, premiums, and renewal dates for your properties."
          actionLabel="Add Policy"
          onAction={() => { setEditingInsurance(null); setIsAddModalOpen(true); }}
          icon="Shield"
        />
      )}

      <Modal
        isOpen={!!editingInsuranceDocument}
        onClose={() => { if (isSavingInsuranceDocument) return; setEditingInsuranceDocument(null); }}
        title="Edit Insurance Document"
        maxWidth="max-w-xl"
        mobileFullScreen
      >
        <form onSubmit={handleSaveInsuranceDocument} className="space-y-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Document Title *</label>
            <input
              type="text"
              value={documentEditForm.title}
              onChange={(event) => setDocumentEditForm(prev => ({ ...prev, title: event.target.value }))}
              className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
              disabled={isSavingInsuranceDocument}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Notes</label>
            <textarea
              value={documentEditForm.notes}
              onChange={(event) => setDocumentEditForm(prev => ({ ...prev, notes: event.target.value }))}
              rows="5"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="Document notes..."
              disabled={isSavingInsuranceDocument}
            />
          </div>
          {editingInsuranceDocument && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-500">
              The uploaded file and its Property / Insurance Policy links will remain unchanged.
            </div>
          )}
          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
            <button type="button" onClick={() => setEditingInsuranceDocument(null)} disabled={isSavingInsuranceDocument} className="min-h-[44px] w-full rounded-xl px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 sm:w-auto">Cancel</button>
            <button type="submit" disabled={isSavingInsuranceDocument} className="min-h-[44px] w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 sm:w-auto">{isSavingInsuranceDocument ? 'Saving...' : 'Save Document'}</button>
          </div>
        </form>
      </Modal>

      <Modal 
        isOpen={isAddModalOpen} 
        onClose={() => { if (isSavingInsurance) return; setIsAddModalOpen(false); setEditingInsurance(null); }} 
        title={editingInsurance ? 'Edit Insurance Policy' : 'Add Insurance Policy'}
        maxWidth="max-w-4xl"
        mobileFullScreen
      >
        <InsuranceForm 
          initialData={editingInsurance}
          existingDocuments={editingInsurance ? allDocuments.filter(doc => String(doc.insurancePolicyId || doc.insuranceId || '') === String(editingInsurance.id) && String(doc.status || '').toLowerCase() !== 'archived') : []}
          onSubmit={handleAddInsurance}
          onCancel={() => { if (isSavingInsurance) return; setIsAddModalOpen(false); setEditingInsurance(null); }}
          isSubmitting={isSavingInsurance}
        />
      </Modal>
    </div>
  );
};

export default InsurancePage;